import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Typography,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Input,
  InputAdornment,
  LinearProgress,
  Menu,
  TableSortLabel,
  CircularProgress,
} from "@mui/material";
import BackdropMeepl from "../../../Ancillary/AnimationComponents/BackdropMeepl";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import SearchIcon from "@mui/icons-material/Search";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import Chart from "react-apexcharts";
import { useSelector, useDispatch } from "react-redux";
import SelectableSearch from "../../../Base/views/SelectableSearch";
import CommonDatePicker from "../../../Base/views/CommonDatePicker";
import { ConvertDate, DateConvertInDbFormat, getUserData, getDomain, getUserId, getEmployeeId } from "../../../Base/helper/baseFunctions";
import { GetShiftTimeConfigureHelper } from "../../../Leave/helper/ShiftTimeConfigureHelper";
import { GetEmploymentTypeHelper } from "../../../Ancillary/EmploymentType/helper/EmploymentTypeHelper";
import { GetUserWorkLocationHelper } from "../../../Employee/helper/EmployeeHelper";
import { GetAttendanceDashboardDataHelper } from "../../helper/Team/TeamHelper";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import CustomToggle from "../../../Base/views/CustomToggle";

const AttendanceDashboard = ({
  isDashboardView,
  setIsDashboardView,
  selectedOption = "",
  handleSelectionChange = () => { },
  lmsExecutive = false,
  onDepartmentRowClick,
  onYetToCheckInClick,
  onLeaveClick,
  onOnDutyClick,
}) => {
  const dispatch = useDispatch();
  const userData = getUserData();
  const currentDate = new Date();
  const defaultDate = DateConvertInDbFormat();
  const { atsModuleData } = useSelector((state) => state?.RoleAndAccessReducer?.ATSModuleReducer);
  const whatsAppFlow = atsModuleData?.find((item) => item.module_name === "Whatsapp Flow");
    
  const reportingManagerEmpId = getEmployeeId() || userData?.employee_id || userData?.emp_id || null;

  // Filters State for Today's Bar Graph (Locked to today's date)
  const [todayFilters, setTodayFilters] = useState({
    shift: [],
    location: [],
    employmentType: [],
  });

  const [appliedTodayFilters, setAppliedTodayFilters] = useState({
    shift: [],
    location: [],
    employmentType: [],
  });

  // Filters State for Attendance Summary (Selectable date)
  const [summaryFilters, setSummaryFilters] = useState({
    date: defaultDate,
    shift: [],
    location: [],
    employmentType: [],
  });

  const [appliedSummaryFilters, setAppliedSummaryFilters] = useState({
    date: defaultDate,
    shift: [],
    location: [],
    employmentType: [],
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState("department");
  const [sortDirection, setSortDirection] = useState("asc");

  // Dropdown Options
  const [workLocations, setWorkLocations] = useState({ load: true, values: [] });
  const [shifts, setShifts] = useState({ load: true, values: [] });
  const [employmentTypes, setEmploymentTypes] = useState({ load: true, values: [] });

  const [loadingToday, setLoadingToday] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [todayData, setTodayData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [fixedDeptData, setFixedDeptData] = useState([]);
  const [todayDownloadAnchorEl, setTodayDownloadAnchorEl] = useState(null);
  const [summaryDownloadAnchorEl, setSummaryDownloadAnchorEl] = useState(null);
  const [progressAnimated, setProgressAnimated] = useState(false);

  const { shift_time_options = [] } = useSelector((state) => state?.LeaveReducer?.ShiftTimeConfigureReducer || {});
  const { employment_type_options = [] } = useSelector((state) => state?.AncillaryReducer?.EmploymentTypeReducer || {});

  useEffect(() => {
    setProgressAnimated(false);
    const timer = setTimeout(() => setProgressAnimated(true), 150);
    return () => clearTimeout(timer);
  }, [todayData, summaryData]);

  // Fetch helper for API
  const fetchDashboardData = (currentFilters, targetDate, isTodaySection) => {
    if (isTodaySection) {
      setLoadingToday(true);
    } else {
      setLoadingSummary(true);
    }

    const reqDomain = userData?.domain || getDomain();
    const reqUserId = userData?.emp_users_pk || getUserId();
    const reportingManagerEmpId = getEmployeeId() || userData?.employee_id || userData?.emp_id || null;

    let locJson = null;
    if (Array.isArray(currentFilters.location) && currentFilters.location.length > 0) {
      const locList = currentFilters.location.map((item) => {
        const locId = typeof item === "object" && item !== null
          ? item.work_location_id
          : item;
        return locId ? { ats_work_location_id: String(locId) } : null;
      }).filter(Boolean);
      if (locList.length > 0) {
        locJson = JSON.stringify(locList);
      }
    }

    let shiftIds = null;
    if (Array.isArray(currentFilters.shift) && currentFilters.shift.length > 0) {
      const ids = currentFilters.shift
        .map((s) => typeof s === "object" && s !== null
          ? s.shift_roster_configuration_id
          : s)
        .filter(Boolean);
      if (ids.length > 0) {
        shiftIds = `{${ids.join(',')}}`;
      }
    }

    let empTypeIds = null;
    if (Array.isArray(currentFilters.employmentType) && currentFilters.employmentType.length > 0) {
      const ids = currentFilters.employmentType
        .map((e) => typeof e === "object" && e !== null
          ? e.employmenttype_id
          : e)
        .filter(Boolean);
      if (ids.length > 0) {
        empTypeIds = `{${ids.join(',')}}`;
      }
    }

    let bUnits = null;
    if (Array.isArray(currentFilters.businessUnit) && currentFilters.businessUnit.length > 0) {
      const ids = currentFilters.businessUnit
        .map((b) => typeof b === "object" && b !== null
          ? b.businessunit_id
          : b)
        .filter(Boolean);
      if (ids.length > 0) {
        bUnits = `{${ids.join(',')}}`;
      }
    }

    GetAttendanceDashboardDataHelper({
      domain_name: reqDomain,
      date: targetDate || defaultDate,
      shift_ids: shiftIds,
      ats_work_location_json: locJson,
      employment_type_ids: empTypeIds,
      business_unit: bUnits,
      reporting_manager_emp_id: reportingManagerEmpId,
      user_id: reqUserId,
    })
      .then((res) => {
        if (isTodaySection) setLoadingToday(false);
        else setLoadingSummary(false);

        const data = res?.data?.data || res?.data || [];
        const resultList = Array.isArray(data) ? data : [];
        if (isTodaySection) {
          setTodayData(resultList);
        } else {
          setSummaryData(resultList);
        }
      })
      .catch(() => {
        if (isTodaySection) {
          setLoadingToday(false);
          setTodayData([]);
        } else {
          setLoadingSummary(false);
          setSummaryData([]);
        }
      });
  };

  // Initial data loading on mount
  useEffect(() => {
    const reqDomain = userData?.domain || getDomain();
    const reqUserId = userData?.emp_users_pk || getUserId();

    GetShiftTimeConfigureHelper({ domain_name: reqDomain }).then((res) => {
      const vals = res?.data?.data || res?.data || [];
      setShifts({ load: false, values: Array.isArray(vals) ? vals : [] });
    }).catch(() => setShifts({ load: false, values: [] }));

    GetEmploymentTypeHelper({ domain_name: reqDomain }).then((res) => {
      const vals = res?.data?.data || res?.data || [];
      setEmploymentTypes({ load: false, values: Array.isArray(vals) ? vals : [] });
    }).catch(() => setEmploymentTypes({ load: false, values: [] }));

    GetUserWorkLocationHelper({ domain_name: reqDomain, user_id: reqUserId }).then((res) => {
      const locs = res?.data?.data || res?.data || [];
      setWorkLocations({ load: false, values: Array.isArray(locs) ? locs : [] });
    }).catch(() => {
      setWorkLocations({ load: false, values: [] });
    });

    // Fetch both sections with 1 single combined request on initial load
    setLoadingToday(true);
    setLoadingSummary(true);
    const reqReportingManagerId = getEmployeeId() || userData?.employee_id || userData?.emp_id || null;

    GetAttendanceDashboardDataHelper({
      domain_name: reqDomain,
      date: defaultDate,
      shift_ids: null,
      ats_work_location_json: null,
      employment_type_ids: null,
      business_unit: null,
      reporting_manager_emp_id: reqReportingManagerId,
      user_id: reqUserId,
    })
      .then((res) => {
        setLoadingToday(false);
        setLoadingSummary(false);
        const data = res?.data?.data || res?.data || [];
        const resultList = Array.isArray(data) ? data : [];
        setTodayData(resultList);
        setSummaryData(resultList);
        setFixedDeptData(resultList);
      })
      .catch(() => {
        setLoadingToday(false);
        setLoadingSummary(false);
        setTodayData([]);
        setSummaryData([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Today Section Filter Handlers
  const handleApplyToday = () => {
    setAppliedTodayFilters(todayFilters);
    fetchDashboardData(todayFilters, defaultDate, true);
  };

  const handleResetToday = () => {
    const initial = {
      shift: [],
      location: [],
      employmentType: [],
    };
    setTodayFilters(initial);
    setAppliedTodayFilters(initial);
    fetchDashboardData(initial, defaultDate, true);
  };

  // Summary Section Filter Handlers
  const handleApplySummary = () => {
    setAppliedSummaryFilters(summaryFilters);
    fetchDashboardData(summaryFilters, summaryFilters.date || defaultDate, false);
  };

  const handleResetSummary = () => {
    const initial = {
      date: defaultDate,
      shift: [],
      location: [],
      employmentType: [],
    };
    setSummaryFilters(initial);
    setAppliedSummaryFilters(initial);
    fetchDashboardData(initial, defaultDate, false);
  };

  // Map Data for Today's Section
  const todayDeptList = (todayData || [])?.map((row) => {
    const departmentId = Number(row?.department_id || 0);
    const departmentName = (row?.department_name || "Unassigned").trim();
    const totalCheckIn = Number(row?.total_check_in || 0);
    const checkedOut = Number(row?.checked_out || 0);
    const onDuty = Number(row?.on_duty || 0);
    const grossExpected = Number(row?.expected || 0);
    const netExpected = Number(row?.net_expected || 0);
    const leaveCount = Number(row?.leave_count || 0);
    const expected = netExpected;
    const yetToCome = Math.max(0, expected - onDuty);
    const attendanceRate = Number(row?.attendance_percentage || (expected > 0 ? Math.round((onDuty / expected) * 100) : 0));

    return {
      department: departmentName,
      department_id: departmentId,
      department_name: departmentName,
      totalCheckIn,
      checkedOut,
      onDuty,
      notCheckedOut: onDuty,
      grossExpected,
      expected,
      netExpected,
      leaveCount,
      plannedLeave: leaveCount,
      yetToCome,
      attendanceRate,
      rawRow: row,
    };
  });

  // Today's Overall Totals
  const todayTotalExpected = todayDeptList.reduce((acc, d) => acc + d.expected, 0);
  const todayTotalCheckIn = todayDeptList.reduce((acc, d) => acc + d.totalCheckIn, 0);
  const todayTotalOnDuty = todayDeptList.reduce((acc, d) => acc + d.onDuty, 0);
  const todayTotalYetToCome = todayDeptList.reduce((acc, d) => acc + d.yetToCome, 0);
  const todayTotalLeave = todayDeptList.reduce((acc, d) => acc + d.leaveCount, 0);

  // Today's Bar Chart Configuration
  const todayChartOptions = {
    chart: {
      type: "bar",
      height: 310,
      toolbar: { show: false },
      fontFamily: "Nunito Sans, sans-serif",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "45%",
        borderRadius: 4,
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: "11px",
        fontFamily: "Nunito Sans, sans-serif",
        fontWeight: 700,
        colors: ["#333"],
      },
    },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    colors: ["#10B981", "#F28E2B", "#8B44FF"],
    xaxis: {
      categories: todayDeptList.map((d) => d.department),
      title: { text: "Business Units" },
    },
    yaxis: {
      title: { text: "No. of Employees" },
    },
    fill: { opacity: 1 },
    tooltip: {
      y: {
        formatter: (val) => `${val} employees`,
      },
    },
    legend: {
      position: "top",
      fontFamily: "Nunito Sans, sans-serif",
      fontWeight: 600,
    },
  };

  const todayChartSeries = [
    {
      name: "On Duty",
      data: todayDeptList.map((d) => d.onDuty),
    },
    {
      name: "Yet to Check-In",
      data: todayDeptList.map((d) => d.yetToCome),
    },
    {
      name: "Expected",
      data: todayDeptList.map((d) => d.expected),
    },
  ];

  // Map Data for Summary Section
  const summaryDeptList = (summaryData || [])?.map((row) => {
    const departmentId = Number(row.department_id || 0);
    const departmentName = (row.department_name || "Unassigned").trim();
    const totalCheckIn = Number(row.total_check_in || 0);
    const checkedOut = Number(row.checked_out || 0);
    const onDuty = Number(row.on_duty || 0);
    const grossExpected = Number(row.expected || 0);
    const netExpected = Number(row.net_expected || 0);
    const leaveCount = Number(row.leave_count || 0);
    const expected = netExpected;
    const yetToCome = Math.max(0, expected - onDuty);
    const attendanceRate = Number(row.attendance_percentage || (expected > 0 ? Math.round((onDuty / expected) * 100) : 0));

    return {
      department: departmentName,
      department_id: departmentId,
      department_name: departmentName,
      totalCheckIn,
      checkedOut,
      onDuty,
      grossExpected,
      expected,
      netExpected,
      leaveCount,
      plannedLeave: leaveCount,
      yetToCome,
      attendanceRate,
      rawRow: row,
    };
  });

  const summaryTotalExpected = summaryDeptList.reduce((acc, d) => acc + d.expected, 0);
  const summaryTotalCheckedIn = summaryDeptList.reduce((acc, d) => acc + d.totalCheckIn, 0);
  const summaryTotalYetToCome = summaryDeptList.reduce((acc, d) => acc + d.yetToCome, 0);
  const summaryTotalLeave = summaryDeptList.reduce((acc, d) => acc + d.leaveCount, 0);
  const summaryTotalAbsent = Math.max(0, summaryTotalExpected - summaryTotalCheckedIn - summaryTotalLeave);

  // Summary Donut Chart Options
  const summaryDonutSeries = [summaryTotalCheckedIn, summaryTotalLeave, summaryTotalAbsent];

  const summaryDonutOptions = {
    chart: {
      type: "donut",
      height: 310,
      fontFamily: "Nunito Sans, sans-serif",
    },
    labels: ["Check-In", "On Leave", "Absent"],
    colors: ["#10B981", "#8B5CF6", "#EF4444"],
    legend: {
      position: "right",
      fontSize: "12px",
      fontFamily: "Nunito Sans, sans-serif",
      fontWeight: 600,
      offsetY: 20,
      markers: {
        width: 10,
        height: 10,
        radius: 10,
      },
      itemMargin: {
        vertical: 4,
      },
      formatter: function (seriesName, opts) {
        const val = opts?.w?.globals?.series?.[opts.seriesIndex] || 0;
        const total = summaryTotalExpected || 0;
        const pct = total > 0 ? ((val / total) * 100).toFixed(0) : 0;
        return `${seriesName}: ${Number(val).toLocaleString("en-US")} (${pct}%)`;
      },
    },
    plotOptions: {
      pie: {
        customScale: 1,
        donut: {
          size: "68%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "13px",
              fontWeight: 600,
              color: "#6b7280",
              offsetY: -5,
            },
            value: {
              show: true,
              fontSize: "22px",
              fontWeight: 700,
              color: "#1a1a1a",
              offsetY: 5,
              formatter: (val) => `${Number(val).toLocaleString("en-US")}`,
            },
            total: {
              show: true,
              showAlways: true,
              label: "Expected",
              fontSize: "13px",
              fontWeight: 700,
              color: "#6b7280",
              formatter: function () {
                return Number(summaryTotalExpected).toLocaleString("en-US");
              },
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      y: {
        formatter: (val) => {
          const total = summaryTotalExpected || 0;
          const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0";
          return `${val} employees (${pct}%)`;
        },
      },
    },
  };

  // Table Data
  const displayDepartments = todayDeptList.length > 0 ? todayDeptList : summaryDeptList;

  // Sorting Handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Filter & Sort Table
  const filteredDepartments = displayDepartments
    .filter((d) => (d.department || "").toLowerCase().includes(searchQuery.toLowerCase().trim()))
    .sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (sortColumn === "attendanceRate") {
        aVal = a.expected ? (a.onDuty / a.expected) * 100 : 0;
        bVal = b.expected ? (b.onDuty / b.expected) * 100 : 0;
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });

  // Filter Badges Generator
  const getFilterBadges = (filtersObj, isToday = false) => {
    const badges = [];
    if (isToday) {
      badges.push({ label: `Date: Today (${ConvertDate(defaultDate)})`, active: true });
    } else if (filtersObj.date) {
      badges.push({ label: `Date: ${ConvertDate(filtersObj.date)}`, active: true });
    }

    if (Array.isArray(filtersObj.location) && filtersObj.location.length > 0) {
      if (filtersObj.location.length === 1) {
        const item = filtersObj.location[0];
        const name = typeof item === "object" ? item.work_location_name : ((workLocations?.values || []).find((l) => String(l.work_location_id) === String(item))?.work_location_name || "Location");
        badges.push({ label: `Location: ${name}`, active: true });
      } else {
        badges.push({ label: "Selected Locations", active: true });
      }
    } else {
      badges.push({ label: "All Locations", active: false });
    }

    if (Array.isArray(filtersObj.employmentType) && filtersObj.employmentType.length > 0) {
      if (filtersObj.employmentType.length === 1) {
        const item = filtersObj.employmentType[0];
        const name = typeof item === "object" ? item.etype_name : ((employmentTypes?.values || employment_type_options || []).find((e) => String(e.employmenttype_id) === String(item))?.etype_name || "Employment Type");
        badges.push({ label: `Type: ${name}`, active: true });
      } else {
        badges.push({ label: "Selected Employment Types", active: true });
      }
    } else {
      badges.push({ label: "All Employment Types", active: false });
    }

    if (Array.isArray(filtersObj.shift) && filtersObj.shift.length > 0) {
      if (filtersObj.shift.length === 1) {
        const item = filtersObj.shift[0];
        const name = typeof item === "object" ? item.shift_name : ((shifts?.values || shift_time_options || []).find((s) => String(s.shift_roster_configuration_id) === String(item))?.shift_name || "Shift");
        badges.push({ label: `Shift: ${name}`, active: true });
      } else {
        badges.push({ label: "Selected Shifts", active: true });
      }
    } else {
      badges.push({ label: "All Shifts", active: false });
    }

    return badges;
  };

  const todayFilterBadges = getFilterBadges(appliedTodayFilters, true);
  const summaryFilterBadges = getFilterBadges(appliedSummaryFilters, false);

  // Download Handlers for Section 1 (Today's Bar Graph)
  const handleDownloadTodayExcel = () => {
    setTodayDownloadAnchorEl(null);
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Business Unit,Expected,On Duty,Yet to Check-In\n";
    todayDeptList.forEach((dept) => {
      csvContent += `"${dept.department}",${dept.expected},${dept.onDuty},${dept.yetToCome}\n`;
    });
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `Today_Attendance_Workforce_${defaultDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadTodayPDF = () => {
    setTodayDownloadAnchorEl(null);
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Today's Attendance Workforce Report", 14, 15);
    doc.setFontSize(9);
    doc.text(`Filters: ${todayFilterBadges.map((b) => b.label).join(" | ")}`, 14, 22);
    doc.text(`Expected: ${todayTotalExpected} | On Duty: ${todayTotalOnDuty} | Yet to Check-In: ${todayTotalYetToCome}`, 14, 27);

    const tableColumn = ["Business Unit", "Expected", "On Duty", "Yet to Check-In", "Attendance %"];
    const tableRows = todayDeptList.map((dept) => [dept.department, dept.expected, dept.onDuty, dept.yetToCome, `${dept.attendanceRate}%`]);

    autoTable(doc, {
      startY: 34,
      head: [tableColumn],
      body: tableRows,
      theme: "striped",
      headStyles: { fillColor: [139, 68, 255] },
    });
    doc.save(`Today_Attendance_Workforce_${defaultDate}.pdf`);
  };

  // Download Handlers for Section 2 (Summary Donut Chart)
  const handleDownloadSummaryExcel = () => {
    setSummaryDownloadAnchorEl(null);
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Metric,Count,Percentage\n";
    csvContent += `"Expected",${summaryTotalExpected},"100%"\n`;
    csvContent += `"Check-In",${summaryTotalCheckedIn},"${summaryTotalExpected > 0 ? Math.round((summaryTotalCheckedIn / summaryTotalExpected) * 100) : 0}%"\n`;
    csvContent += `"On Leave",${summaryTotalLeave},"${summaryTotalExpected > 0 ? Math.round((summaryTotalLeave / summaryTotalExpected) * 100) : 0}%"\n`;
    csvContent += `"Absent",${summaryTotalAbsent},"${summaryTotalExpected > 0 ? Math.round((summaryTotalAbsent / summaryTotalExpected) * 100) : 0}%"\n`;

    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `Attendance_Summary_${appliedSummaryFilters.date || defaultDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadSummaryPDF = () => {
    setSummaryDownloadAnchorEl(null);
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Attendance Summary Distribution Report", 14, 15);
    doc.setFontSize(9);
    doc.text(`Filters: ${summaryFilterBadges.map((b) => b.label).join(" | ")}`, 14, 22);
    doc.text(`Expected: ${summaryTotalExpected} | Check-In: ${summaryTotalCheckedIn} | On Leave: ${summaryTotalLeave} | Absent: ${summaryTotalAbsent}`, 14, 27);

    const tableColumn = ["Status Category", "Employee Count", "Percentage"];
    const tableRows = [
      ["Check-In", summaryTotalCheckedIn, `${summaryTotalExpected > 0 ? Math.round((summaryTotalCheckedIn / summaryTotalExpected) * 100) : 0}%`],
      ["On Leave", summaryTotalLeave, `${summaryTotalExpected > 0 ? Math.round((summaryTotalLeave / summaryTotalExpected) * 100) : 0}%`],
      ["Absent", summaryTotalAbsent, `${summaryTotalExpected > 0 ? Math.round((summaryTotalAbsent / summaryTotalExpected) * 100) : 0}%`],
      ["Total Expected", summaryTotalExpected, "100%"],
    ];

    autoTable(doc, {
      startY: 34,
      head: [tableColumn],
      body: tableRows,
      theme: "striped",
      headStyles: { fillColor: [139, 68, 255] },
    });
    doc.save(`Attendance_Summary_${appliedSummaryFilters.date || defaultDate}.pdf`);
  };

  return (
    <Box sx={{ p: 2, backgroundColor: "#fff", minHeight: "100%", position: "relative" }}>
      <BackdropMeepl open={loadingToday && loadingSummary} dark={false} />

      {/* PAGE HEADER */}
      <Box sx={{ mb: 2.5, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
            Attendance Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Track attendance, shift planning, and complete attendance distribution across business units.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {!lmsExecutive && handleSelectionChange && (
            <Tooltip title={"Get team time link"} placement="top">
              <Typography size="small" variant="span" sx={{ display: "inline-grid" }}>
                <Select
                    value={selectedOption}
                    size="small"
                    className="mpl-primary-btn"
                    onChange={handleSelectionChange}
                    displayEmpty
                    sx={{
                        width: "135px", height: "37px",
                        fontWeight: "bold", "& .MuiSelect-select": {
                            padding: "2px",
                            paddingLeft: "4px"
                        },
                    }}
                >
                    <MenuItem value="" disabled>Link Options</MenuItem>
                    <MenuItem value="team">
                        <Tooltip title="Team Time Common portal" placement="left" arrow>
                            <span style={{ width: "100%", display: "inline-block" }}>Get Team Time</span>
                        </Tooltip>
                    </MenuItem>
                    {whatsAppFlow && (
                        <MenuItem value="whatsapp">
                            <Tooltip title="By using this link admin can mark attendance through WhatsApp for teams." placement="left" arrow>
                                <span style={{ width: "100%", display: "inline-block" }}>Get WhatsApp Attendance Flow</span>
                            </Tooltip>
                        </MenuItem>
                    )}
                </Select>
              </Typography>
            </Tooltip>
          )}
          {setIsDashboardView && (
            <CustomToggle
              isChecked={!isDashboardView}
              handleToggleChange={() => setIsDashboardView(!isDashboardView)}
              Yestext={"Table"}
              NoText={"Dashboard"}
              YesTooltip={"Switch to Table View"}
              NoTooltip={"Switch to Dashboard View"}
              style={{ height: "37px", labelPadding: "2px 8px" }}
            />
          )}
        </Box>
      </Box>

      {/* ========================================================================= */}
      {/* SECTION 1: TODAY'S WORKFORCE STATUS & BAR GRAPH                           */}
      {/* ========================================================================= */}
      <Box sx={{ mb: 4, position: "relative" }}>
        {loadingToday && !loadingSummary && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "10px",
            }}
          >
            <CircularProgress size={36} sx={{ color: "#8B44FF" }} />
          </Box>
        )}
        {/* Section 1 Filter Bar */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: "10px", boxShadow: "0 0 10px 0 #b1b2b726", border: "1px solid #e5d9f7", backgroundColor: "#f4effc" }}>
          <Grid container spacing={2} alignItems="center">
            {/* 1. Location */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" sx={{ backgroundColor: "#fff", borderRadius: "4px" }}>
                <SelectableSearch
                  multiple
                  limitTags={1}
                  label="Location"
                  placeholder="All Locations"
                  value={todayFilters.location}
                  fieldLabel="work_location_name"
                  variant="outlined"
                  name="location"
                  size="small"
                  loading={() => { }}
                  onChangeEvent={(name, val) => setTodayFilters({ ...todayFilters, location: val || [] })}
                  data={workLocations}
                  isOptionEqualToValue={(opt, val) => {
                    if (!opt || !val) return false;
                    const optId = opt.work_location_id;
                    const valId = val.work_location_id;
                    return Boolean(optId && valId && String(optId) === String(valId));
                  }}
                  customBackground
                />
              </FormControl>
            </Grid>

            {/* 2. Employment Type */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" sx={{ backgroundColor: "#fff", borderRadius: "4px" }}>
                <SelectableSearch
                  multiple
                  limitTags={1}
                  label="Employment Type"
                  placeholder="All Employment Types"
                  value={todayFilters.employmentType}
                  fieldLabel="etype_name"
                  variant="outlined"
                  name="employmentType"
                  size="small"
                  loading={() => { }}
                  onChangeEvent={(name, val) => setTodayFilters({ ...todayFilters, employmentType: val || [] })}
                  data={
                    employmentTypes?.values?.length > 0
                      ? {
                        load: false,
                        values: employmentTypes.values.map((e) => ({
                          ...e,
                          etype_name: e?.etype_name || e?.name || e?.employment_type_name || "Employment Type",
                        })),
                      }
                      : {
                        load: false,
                        values: (employment_type_options || []).map((e) => ({
                          ...e,
                          etype_name: e?.etype_name || e?.name || e?.employment_type_name || "Employment Type",
                        })),
                      }
                  }
                  isOptionEqualToValue={(opt, val) => {
                    if (!opt || !val) return false;
                    const optId = opt.employmenttype_id;
                    const valId = val.employmenttype_id;
                    return Boolean(optId && valId && String(optId) === String(valId));
                  }}
                  customBackground
                />
              </FormControl>
            </Grid>

            {/* 3. Shift */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" sx={{ backgroundColor: "#fff", borderRadius: "4px" }}>
                <SelectableSearch
                  /* multiple */
                  limitTags={1}
                  label="Shift"
                  placeholder="All Shifts"
                  value={Array.isArray(todayFilters.shift) && todayFilters.shift.length > 0 ? todayFilters.shift[0] : (todayFilters.shift || null)}
                  fieldLabel="shift_name"
                  variant="outlined"
                  name="shift"
                  size="small"
                  loading={() => { }}
                  onChangeEvent={(name, val) => setTodayFilters({ ...todayFilters, shift: val ? (Array.isArray(val) ? val : [val]) : [] })}
                  data={shifts?.values?.length > 0 ? shifts : { load: false, values: shift_time_options || [] }}
                  isOptionEqualToValue={(opt, val) => {
                    if (!opt || !val) return false;
                    const optId = opt.shift_roster_configuration_id;
                    const valId = val.shift_roster_configuration_id;
                    return Boolean(optId && valId && String(optId) === String(valId));
                  }}
                  customBackground
                />
              </FormControl>
            </Grid>

            {/* 4. Apply & Reset */}
            <Grid item xs={12} sm={12} md={3} sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                className="mpl-primary-btn"
                onClick={handleApplyToday}
                sx={{ fontWeight: 700, borderRadius: "8px", textTransform: "none", height: "38px", px: 2 }}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleResetToday}
                startIcon={<ClearIcon />}
                sx={{ fontWeight: 700, borderRadius: "8px", textTransform: "none", height: "38px", borderColor: "#e0e0e0", backgroundColor: "#fff", px: 1.5 }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Today's KPI Metric Cards */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          {/* 1. Expected */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ display: "block", overflow: "hidden", position: "relative", height: 100 }}>
              <CardContent sx={{ padding: "12px !important" }}>
                <Box display="flex" alignItems="center" gap={0.6} sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: 14 }} variant="subtitle2" color="text.primary">
                    Expected
                  </Typography>
                  <Tooltip title="Total expected headcount scheduled for today (excluding leave)." arrow placement="top">
                    <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary", cursor: "pointer", fontSize: 16 }} />
                  </Tooltip>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h3" gutterBottom>
                    {todayTotalExpected.toLocaleString("en-US")}
                  </Typography>
                  <GroupsOutlinedIcon
                    className="card-icon"
                    sx={{
                      fontSize: "62px !important",
                      height: "62px !important",
                      width: "62px !important",
                      color: "#8B44FF",
                      opacity: 0.8,
                      bottom: "-10px",
                      right: "7px",
                      position: "absolute",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 2. On Duty */}
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title={onOnDutyClick ? "Click to view in Team Time" : ""} arrow placement="top">
              <Card
                onClick={() => onOnDutyClick && onOnDutyClick({ filters: appliedTodayFilters, date: defaultDate })}
                sx={{
                  display: "block",
                  overflow: "hidden",
                  position: "relative",
                  height: 100,
                  cursor: onOnDutyClick ? "pointer" : "default",
                }}
              >
                <CardContent sx={{ padding: "12px !important" }}>
                  <Box display="flex" alignItems="center" gap={0.6} sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: 14 }} variant="subtitle2" color="text.primary">
                      On Duty
                    </Typography>
                    <Tooltip title="Employees currently on duty today." arrow placement="top">
                      <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary", cursor: "pointer", fontSize: 16 }} />
                    </Tooltip>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h3" gutterBottom>
                      {todayTotalOnDuty.toLocaleString("en-US")}
                    </Typography>
                    <HowToRegIcon
                      className="card-icon"
                      sx={{
                        fontSize: "62px !important",
                        height: "62px !important",
                        width: "62px !important",
                        color: "#10B981",
                        opacity: 0.8,
                        bottom: "-10px",
                        right: "7px",
                        position: "absolute",
                        pointerEvents: "none",
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>

          {/* 3. On Leave */}
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title={onLeaveClick ? "Click to view On Leave details" : ""} arrow placement="top">
              <Card
                onClick={() => onLeaveClick && onLeaveClick({ filters: appliedTodayFilters, reporting_manager_emp_id: reportingManagerEmpId })}
                sx={{
                  display: "block",
                  overflow: "hidden",
                  position: "relative",
                  height: 100,
                  cursor: onLeaveClick ? "pointer" : "default",
                }}
              >
                <CardContent sx={{ padding: "12px !important" }}>
                  <Box display="flex" alignItems="center" gap={0.6} sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: 14 }} variant="subtitle2" color="text.primary">
                      On Leave
                    </Typography>
                    <Tooltip title="Employees on approved leave today." arrow placement="top">
                      <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary", cursor: "pointer", fontSize: 16 }} />
                    </Tooltip>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h3" gutterBottom>
                      {todayTotalLeave.toLocaleString("en-US")}
                    </Typography>
                    <EventBusyOutlinedIcon
                      className="card-icon"
                      sx={{
                        fontSize: "62px !important",
                        height: "62px !important",
                        width: "62px !important",
                        color: "#8B5CF6",
                        opacity: 0.8,
                        bottom: "-10px",
                        right: "7px",
                        position: "absolute",
                        pointerEvents: "none",
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>

          {/* 4. Yet to Check-In */}
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title={onYetToCheckInClick ? "Click to view Yet to Check-In details" : ""} arrow placement="top">
              <Card
                onClick={() => onYetToCheckInClick && onYetToCheckInClick({ filters: appliedTodayFilters, reporting_manager_emp_id: reportingManagerEmpId })}
                sx={{
                  display: "block",
                  overflow: "hidden",
                  position: "relative",
                  height: 100,
                  cursor: onYetToCheckInClick ? "pointer" : "default",
                }}
              >
                <CardContent sx={{ padding: "12px !important" }}>
                  <Box display="flex" alignItems="center" gap={0.6} sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: 14 }} variant="subtitle2" color="text.primary">
                      Yet to Check-In
                    </Typography>
                    <Tooltip title="Employees planned who have not checked in yet." arrow placement="top">
                      <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary", cursor: "pointer", fontSize: 16 }} />
                    </Tooltip>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h3" gutterBottom>
                      {todayTotalYetToCome.toLocaleString("en-US")}
                    </Typography>
                    <AccessTimeOutlinedIcon
                      className="card-icon"
                      sx={{
                        fontSize: "62px !important",
                        height: "62px !important",
                        width: "62px !important",
                        color: "#F28E2B",
                        opacity: 0.8,
                        bottom: "-10px",
                        right: "7px",
                        position: "absolute",
                        pointerEvents: "none",
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
        </Grid>

        {/* Today's Bar Chart Card */}
        <Card sx={{ display: "flex", flexDirection: "column", backgroundColor: "#ffffff", borderRadius: "10px", border: "1px solid #e5d9f7" }}>
          <CardHeader
            sx={{ backgroundColor: "#ffffff", py: 1.2, px: 2, borderBottom: "1px solid #f0f0f0" }}
            title={
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: "1rem" }}>
                  Today's Business Unit-wise Workforce Breakdown
                </Typography>
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.8}>
                  {todayFilterBadges.map((badge, idx) => (
                    <Chip key={idx} label={badge.label} size="small" variant="outlined" sx={{ fontSize: "0.72rem", height: "22px", borderColor: "#e0e0e0", color: "text.secondary", backgroundColor: "#f8f9fa", fontWeight: 500 }} />
                  ))}
                </Box>
              </Box>
            }
            action={
              <>
                <Button variant="outlined" size="small" onClick={(e) => setTodayDownloadAnchorEl(e.currentTarget)} startIcon={<CloudDownloadIcon />} sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px", backgroundColor: "#fff" }}>
                  Download
                </Button>
                <Menu anchorEl={todayDownloadAnchorEl} open={Boolean(todayDownloadAnchorEl)} onClose={() => setTodayDownloadAnchorEl(null)}>
                  <MenuItem onClick={handleDownloadTodayExcel}>Download Excel</MenuItem>
                  <MenuItem onClick={handleDownloadTodayPDF}>Download PDF</MenuItem>
                </Menu>
              </>
            }
          />
          <Box id="attendance-today-chart" sx={{ flexGrow: 1, backgroundColor: "#ffffff", px: 2, pt: 1, pb: 1 }}>
            <Chart options={todayChartOptions} series={todayChartSeries} type="bar" height={310} />
          </Box>
        </Card>
      </Box>

      {/* ========================================================================= */}
      {/* SECTION 2: ATTENDANCE SUMMARY DISTRIBUTION (Full Width Donut Chart)       */}
      {/* ========================================================================= */}
      {/*<Box sx={{ mb: 4, position: "relative" }}>
        {loadingSummary && !loadingToday && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "10px",
            }}
          >
            <CircularProgress size={36} sx={{ color: "#8B44FF" }} />
          </Box>
        )}
        
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: "1.15rem" }}>
            Attendance Summary Distribution
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Analyze attendance ratios, leaves, and absences for the selected date.
          </Typography>
        </Box>

        <Paper sx={{ p: 2, mb: 2, borderRadius: "10px", boxShadow: "0 0 10px 0 #b1b2b726", border: "1px solid #e5d9f7", backgroundColor: "#f4effc" }}>
          <Grid container spacing={2} alignItems="center">
         
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small" sx={{ backgroundColor: "#fff", borderRadius: "4px" }}>
                <CommonDatePicker
                  name="date"
                  label="Date"
                  size="small"
                  pickerType="date"
                  pickerFormat="dd/MM/yyyy"
                  DefaultDTvalue={summaryFilters.date}
                  maxDate={currentDate}
                  handleChange={(val) => {
                    const formatted = val
                      ? `${val.getFullYear()}-${("0" + (val.getMonth() + 1)).slice(-2)}-${("0" + val.getDate()).slice(-2)}`
                      : defaultDate;
                    setSummaryFilters({ ...summaryFilters, date: formatted });
                  }}
                />
              </FormControl>
            </Grid>

          
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small" sx={{ backgroundColor: "#fff", borderRadius: "4px" }}>
                <SelectableSearch
                  multiple
                  limitTags={1}
                  label="Location"
                  placeholder="All Locations"
                  value={summaryFilters.location}
                  fieldLabel="work_location_name"
                  variant="outlined"
                  name="location"
                  size="small"
                  loading={() => { }}
                  onChangeEvent={(name, val) => setSummaryFilters({ ...summaryFilters, location: val || [] })}
                  data={workLocations}
                  isOptionEqualToValue={(opt, val) => {
                    if (!opt || !val) return false;
                    const optId = opt.work_location_id;
                    const valId = val.work_location_id;
                    return Boolean(optId && valId && String(optId) === String(valId));
                  }}
                  customBackground
                />
              </FormControl>
            </Grid>

           
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small" sx={{ backgroundColor: "#fff", borderRadius: "4px" }}>
                <SelectableSearch
                  multiple
                  limitTags={1}
                  label="Employment Type"
                  placeholder="All Employment Types"
                  value={summaryFilters.employmentType}
                  fieldLabel="etype_name"
                  variant="outlined"
                  name="employmentType"
                  size="small"
                  loading={() => { }}
                  onChangeEvent={(name, val) => setSummaryFilters({ ...summaryFilters, employmentType: val || [] })}
                  data={
                    employmentTypes?.values?.length > 0
                      ? {
                        load: false,
                        values: employmentTypes.values.map((e) => ({
                          ...e,
                          etype_name: e?.etype_name || e?.name || e?.employment_type_name || "Employment Type",
                        })),
                      }
                      : {
                        load: false,
                        values: (employment_type_options || []).map((e) => ({
                          ...e,
                          etype_name: e?.etype_name || e?.name || e?.employment_type_name || "Employment Type",
                        })),
                      }
                  }
                  isOptionEqualToValue={(opt, val) => {
                    if (!opt || !val) return false;
                    const optId = opt.employmenttype_id;
                    const valId = val.employmenttype_id;
                    return Boolean(optId && valId && String(optId) === String(valId));
                  }}
                  customBackground
                />
              </FormControl>
            </Grid>


            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small" sx={{ backgroundColor: "#fff", borderRadius: "4px" }}>
                <SelectableSearch
                 
                  limitTags={1}
                  label="Shift"
                  placeholder="All Shifts"
                  value={Array.isArray(summaryFilters.shift) && summaryFilters.shift.length > 0 ? summaryFilters.shift[0] : (summaryFilters.shift || null)}
                  fieldLabel="shift_name"
                  variant="outlined"
                  name="shift"
                  size="small"
                  loading={() => { }}
                  onChangeEvent={(name, val) => setSummaryFilters({ ...summaryFilters, shift: val ? (Array.isArray(val) ? val : [val]) : [] })}
                  data={shifts?.values?.length > 0 ? shifts : { load: false, values: shift_time_options || [] }}
                  isOptionEqualToValue={(opt, val) => {
                    if (!opt || !val) return false;
                    const optId = opt.shift_roster_configuration_id;
                    const valId = val.shift_roster_configuration_id;
                    return Boolean(optId && valId && String(optId) === String(valId));
                  }}
                  customBackground
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={2.4} sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                className="mpl-primary-btn"
                onClick={handleApplySummary}
                sx={{ fontWeight: 700, borderRadius: "8px", textTransform: "none", height: "38px", px: 2 }}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleResetSummary}
                startIcon={<ClearIcon />}
                sx={{ fontWeight: 700, borderRadius: "8px", textTransform: "none", height: "38px", borderColor: "#e0e0e0", backgroundColor: "#fff", px: 1.5 }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Paper>
        <Card sx={{ display: "flex", flexDirection: "column", backgroundColor: "#ffffff", borderRadius: "10px", border: "1px solid #e5d9f7" }}>
          <CardHeader
            sx={{ backgroundColor: "#ffffff", py: 1.2, px: 2, borderBottom: "1px solid #f0f0f0" }}
            title={
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: "1rem" }}>
                  Attendance Breakdown
                </Typography>
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.8}>
                  {summaryFilterBadges.map((badge, idx) => (
                    <Chip key={idx} label={badge.label} size="small" variant="outlined" sx={{ fontSize: "0.72rem", height: "22px", borderColor: "#e0e0e0", color: "text.secondary", backgroundColor: "#f8f9fa", fontWeight: 500 }} />
                  ))}
                </Box>
              </Box>
            }
            action={
              <>
                <Button variant="outlined" size="small" onClick={(e) => setSummaryDownloadAnchorEl(e.currentTarget)} startIcon={<CloudDownloadIcon />} sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px", backgroundColor: "#fff" }}>
                  Download
                </Button>
                <Menu anchorEl={summaryDownloadAnchorEl} open={Boolean(summaryDownloadAnchorEl)} onClose={() => setSummaryDownloadAnchorEl(null)}>
                  <MenuItem onClick={handleDownloadSummaryExcel}>Download Excel</MenuItem>
                  <MenuItem onClick={handleDownloadSummaryPDF}>Download PDF</MenuItem>
                </Menu>
              </>
            }
          />
          <Box id="attendance-summary-chart" sx={{ flexGrow: 1, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", py: 2, px: 2 }}>
            <Box sx={{ width: "100%", maxWidth: 650 }}>
              <Chart key={`donut-summary-${summaryTotalExpected}`} options={summaryDonutOptions} series={summaryDonutSeries} type="donut" width="100%" height={310} />
            </Box>
          </Box>
        </Card>
      </Box>*/}

      {/* ========================================================================= */}
      {/* SECTION 3: DEPARTMENT WISE DETAILS TABLE                                  */}
      {/* ========================================================================= */}
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e5d9f7", borderRadius: "10px", backgroundColor: "#fff", boxShadow: "0 0 10px 0 #b1b2b726", mb: 2 }}>
        {/* Top Header Bar inside TableContainer */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, py: 1.5, backgroundColor: "#f4effc", borderBottom: "1px solid #e5d9f7" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
            Business Unit Wise Details
          </Typography>

          {/* Underlined Search Input */}
          <Input
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            }
            endAdornment={
              searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ p: 0.3 }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
            sx={{
              width: 220,
              fontSize: "13px",
              "&:after": { borderBottomColor: "#8b44ff" },
            }}
          />
        </Box>

        <Table>
          <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortColumn === "department"}
                  direction={sortColumn === "department" ? sortDirection : "asc"}
                  onClick={() => handleSort("department")}
                >
                  Business Unit
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <TableSortLabel
                    active={sortColumn === "leaveCount"}
                    direction={sortColumn === "leaveCount" ? sortDirection : "asc"}
                    onClick={() => handleSort("leaveCount")}
                  >
                    On Leave
                  </TableSortLabel>
                  <Tooltip title="Employees on approved leave" arrow placement="top">
                    <InfoOutlinedIcon fontSize="small" sx={{ fontSize: 14, ml: 0.5, color: "text.secondary", cursor: "pointer" }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <TableSortLabel
                    active={sortColumn === "expected"}
                    direction={sortColumn === "expected" ? sortDirection : "asc"}
                    onClick={() => handleSort("expected")}
                  >
                    Expected 
                  </TableSortLabel>
                  <Tooltip title="Employees expected to work today (excluding leave)" arrow placement="top">
                    <InfoOutlinedIcon fontSize="small" sx={{ fontSize: 14, ml: 0.5, color: "text.secondary", cursor: "pointer" }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <TableSortLabel
                    active={sortColumn === "onDuty"}
                    direction={sortColumn === "onDuty" ? sortDirection : "asc"}
                    onClick={() => handleSort("onDuty")}
                  >
                    On Duty
                  </TableSortLabel>
                  <Tooltip title="Employees who have checked in / are present" arrow placement="top">
                    <InfoOutlinedIcon fontSize="small" sx={{ fontSize: 14, ml: 0.5, color: "text.secondary", cursor: "pointer" }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <TableSortLabel
                    active={sortColumn === "yetToCome"}
                    direction={sortColumn === "yetToCome" ? sortDirection : "asc"}
                    onClick={() => handleSort("yetToCome")}
                  >
                    Yet to Check-In
                  </TableSortLabel>
                  <Tooltip title="Expected employees who haven't checked in" arrow placement="top">
                    <InfoOutlinedIcon fontSize="small" sx={{ fontSize: 14, ml: 0.5, color: "text.secondary", cursor: "pointer" }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="left" sx={{ fontWeight: 700, width: "35%" }}>
                <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                  <TableSortLabel
                    active={sortColumn === "attendanceRate"}
                    direction={sortColumn === "attendanceRate" ? sortDirection : "asc"}
                    onClick={() => handleSort("attendanceRate")}
                  >
                    Attendance %
                  </TableSortLabel>
                  <Tooltip title="On Duty ÷ Expected × 100" arrow placement="top">
                    <InfoOutlinedIcon fontSize="small" sx={{ fontSize: 14, ml: 0.5, color: "text.secondary", cursor: "pointer" }} />
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDepartments.length > 0 ? (
              filteredDepartments.map((row, index) => {
                const isUnassigned = (row.department || "").toLowerCase().trim() === "unassigned" || row.department_id === 0;
                return (
                  <TableRow
                    key={index}
                    hover={!isUnassigned}
                    onClick={() => !isUnassigned && onDepartmentRowClick && onDepartmentRowClick({ ...row, selectedDate: defaultDate, filters: appliedTodayFilters }, defaultDate)}
                    sx={{
                      cursor: isUnassigned ? "default" : "pointer",
                      "&:hover": {
                        backgroundColor: isUnassigned ? "inherit !important" : "#f3edfd !important",
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{row.department}</TableCell>
                    <TableCell align="center">{row.leaveCount ?? row.plannedLeave ?? 0}</TableCell>
                    <TableCell align="center">{row.expected}</TableCell>
                    <TableCell align="center">{row.onDuty}</TableCell>
                    <TableCell align="center">{row.yetToCome}</TableCell>
                    <TableCell align="left">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ minWidth: 35 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                            {`${row.attendanceRate}%`}
                          </Typography>
                        </Box>
                        <Box sx={{ width: "100%", minWidth: 100 }}>
                          <LinearProgress
                            variant="determinate"
                            value={progressAnimated ? Math.min(100, row.attendanceRate) : 0}
                            sx={{
                              height: 10,
                              borderRadius: 5,
                              backgroundColor: "#e8f5e9",
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 5,
                                backgroundColor: "#36b37e",
                                transition: "transform 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No matching business units found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AttendanceDashboard;
